"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "@/components/ui/LocaleLink";
import Image from "next/image";
import { logoutAction } from "@/lib/actions/auth";
import { QuotaRing } from "@/components/chat/QuotaRing";
import { useChatQuota } from "@/lib/hooks/use-chat-quota";
import { ChartBar, ChatCircleDots, EnvelopeSimple, SignOut, SquaresFour, ShieldCheck } from "@phosphor-icons/react/dist/ssr";
import type { Icon } from "@phosphor-icons/react";

const ICON_MAP: Record<string, Icon> = {
  dashboard: SquaresFour,
  leads: EnvelopeSimple,
  admin: ShieldCheck,
  overview: ChartBar,
  chat: ChatCircleDots,
};

interface DropdownLink {
  href: string;
  label: string;
  badge?: number;
  icon?: string;
}

interface AccountDropdownProps {
  displayName: string;
  avatarUrl: string | null;
  links: DropdownLink[];
  hasNotification?: boolean;
}

/**
 * The chat allowance line under the AI Chat entry (owner, 2026-08-27).
 * Fetched LAZILY when the dropdown first opens — the nav renders on every
 * page and a per-render quota read would put a Supabase round-trip in front
 * of all of them. Renders nothing until (and unless) the numbers arrive.
 */
function ChatQuotaLine({ label }: { label: (n: number) => string }) {
  const [quota] = useChatQuota();
  if (!quota) return null;
  return (
    <div className="px-13 pb-5 pl-[37px]">
      <QuotaRing used={quota.used} cap={quota.cap} size={14} label={label(Math.max(0, quota.cap - quota.used))} />
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function AccountDropdown({
  displayName,
  avatarUrl,
  links,
  hasNotification,
}: AccountDropdownProps) {
  const t = useTranslations("nav");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }
  }, [open]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }
  }, [open]);

  const initials = getInitials(displayName);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-8 rounded-control border border-navy/20 bg-transparent px-13 py-8 text-sm font-semibold uppercase tracking-[0.04em] text-navy transition-colors hover:border-navy/50 hover:bg-navy/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        {/* Avatar with optional gold ring + notification dot */}
        <span className="relative">
          <span
            className={[
              "flex h-[22px] w-[22px] shrink-0 items-center justify-center overflow-hidden rounded-full text-[10px] font-bold",
              avatarUrl ? (hasNotification ? "ring-2 ring-gold" : "") : "bg-navy/10 text-navy",
            ].join(" ")}
          >
            {avatarUrl ? (
              <Image
                src={avatarUrl}
                alt=""
                width={22}
                height={22}
                className="h-full w-full object-cover"
              />
            ) : (
              initials
            )}
          </span>
          {hasNotification && (
            <span
              aria-label={t("newLeads")}
              className="absolute -right-[2px] -top-[2px] h-[10px] w-[10px] rounded-full border-2 border-white bg-gold"
            />
          )}
        </span>
        {t("account")}
        <svg
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          aria-hidden="true"
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-5 min-w-[180px] rounded-pop border border-card-line bg-white p-8 shadow-e4"
        >
          {links.map((link) => {
            const Icon = link.icon ? ICON_MAP[link.icon] : null;
            return (
            <span key={link.href} className="block">
            <Link
              href={link.href}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-8 rounded-control px-13 py-8 text-sm text-navy transition-colors hover:bg-navy/5"
            >
              {Icon && <Icon size={16} className="shrink-0 opacity-60" />}
              {link.label}
              {link.badge != null && link.badge > 0 && (
                <span className="inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-gold px-5 text-[11px] font-bold text-navy">
                  {link.badge}
                </span>
              )}
            </Link>
            {/* The allowance line rides under the AI Chat entry only, and only
                while the menu is open — so the fetch happens on open, never on
                page render. */}
            {link.icon === "chat" && <ChatQuotaLine label={(n) => t("chatQuota", { n })} />}
            </span>
            );
          })}
          <div className="my-5 border-t border-navy/10" role="separator" />
          <form action={logoutAction}>
            <button
              type="submit"
              role="menuitem"
              className="flex w-full items-center gap-8 rounded-control px-13 py-8 text-sm text-navy transition-colors hover:bg-navy/5"
            >
              <SignOut size={16} className="shrink-0 opacity-60" />
              {t("signOut")}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
